/**
 * Azure B2C MSAL Configuration
 *
 * Configuration is automatically loaded based on the current environment (local/dev/qa/prod)
 * Values are pulled from checkVentory.js or uVerify.js config file
 *
 * To update Azure B2C settings:
 * 1. Go to App/config/checkVentory.js or App/config/uVerify.js
 * 2. Update the azureB2C section for your environment
 * 3. Ensure user flows are configured in Azure Portal > Azure AD B2C
 */

import environment from "./env";

// Get current environment config
const getEnvConfig = () => {
    const env = environment[process.env.HOST];
    return env?.azureB2C || {};
};

const envConfig = getEnvConfig();

// Build B2C authority URLs for different user flows
const buildB2CAuthority = (userFlow) => {
    if (!envConfig.tenant || !envConfig.tenantDomain || !userFlow) {
        console.warn("Azure B2C config incomplete, check checkVentory.js");
        return null;
    }
    return `https://${envConfig.tenant}.b2clogin.com/${envConfig.tenantDomain}/${userFlow}`;
};

export const msalConfig = {
    auth: {
        clientId: envConfig.clientId, // From checkVentory.js
        authority: buildB2CAuthority(envConfig.signInFlow), // B2C sign-in flow
        knownAuthorities: [
            `${envConfig.tenant}.b2clogin.com` // B2C domain
        ],
        redirectUri: window.location.origin, // Must match redirect URI in Azure B2C
        postLogoutRedirectUri: window.location.origin, // Where to go after logout
        navigateToLoginRequestUrl: false // B2C best practice
    },
    cache: {
        cacheLocation: "localStorage", // Options: "sessionStorage" or "localStorage"
        storeAuthStateInCookie: false // Set to true for IE 11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                // Only log errors and warnings in production, disable info/verbose
                switch (level) {
                    case 0: // LogLevel.Error
                        console.error(message);
                        return;
                    case 1: // LogLevel.Warning
                        console.warn(message);
                        return;
                    case 2: // LogLevel.Info
                    case 3: // LogLevel.Verbose
                        // Disabled - too verbose for production
                        return;
                }
            },
            piiLoggingEnabled: false
        }
    }
};

/**
 * App name for forwardedFrom parameter in B2C authorization URLs
 */
export const APP_NAME = "checkventory";

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * For B2C, use openid and offline_access scopes
 */
export const loginRequest = {
    scopes: ["openid", "offline_access"], // B2C scopes
    extraQueryParameters: {
        forwardedFrom: APP_NAME
    }
};

/**
 * B2C User Flow Authorities
 * These are used for different B2C operations
 */
export const b2cPolicies = {
    signIn: buildB2CAuthority(envConfig.signInFlow),
    resetPassword: buildB2CAuthority(envConfig.resetPasswordFlow),
    editProfile: buildB2CAuthority(envConfig.editProfileFlow)
};

/**
 * API scopes - if you have a protected API
 * Format: https://{tenant}.onmicrosoft.com/{api-client-id}/{scope}
 */
export const apiScopes = {
    read: [`https://${envConfig.tenantDomain}/${envConfig.clientId}/read`],
    write: [`https://${envConfig.tenantDomain}/${envConfig.clientId}/write`]
};
